import { createSlice, PayloadAction, Slice } from '@reduxjs/toolkit';
import { Stations } from '../../types/station';

export interface UserState {
  user: {
    signedIn: boolean;
    id: number;
    ebookId: number;
    name: string;
    country: {
      name: string;
      isocode: string;
      timezone: string;
    };
    station: string | null;
    stations: Stations;
    username: string;
    password: null;
    permissions: {
      selectReturnLocation: boolean;
      locationMap: boolean;
      showOnlyCarsForOpenLocations: boolean;
      showOnlyCarsWithKeyboxcode: boolean;
      mapSelectCarReservation: boolean;
      mapSelectCarAgreement: boolean;
      showPriceTable: boolean;
      ignoreOpeningHours: boolean;
      sendAgreementExtraConfirmation: boolean;
      manualPayerInput: boolean;
      manualReturnLocationAddressAllowed: boolean;
      manualPickupLocationAddressAllowed: boolean;
      advancedCheckin: boolean;
      showEmailField: boolean;
      showCheckoutMobileNumberField: boolean;
      showReservationMobileNumberField: boolean;
      allowSelectChargedCarGroup: boolean;
      cdpInput: boolean;
      hccInput: boolean;
      tariffInput: boolean;
      voucherAccountNumberInput: boolean;
      voucherAmountInput: boolean;
      voucherReferenceInput: boolean;
      voucherNumberInput: boolean;
      licenseNumberInput: boolean;
      pciCreditCardInput: boolean;
      equipment: boolean;
      reservationToAgreement: boolean;
      carSearch: boolean;
      driverInsurance: boolean;
      checkoutCarFirst: boolean;
      showEstimatedPrice: boolean;
      reservationPayerRequired: boolean;
      workshopAnalysis: boolean;
      showDriverLicenseInputField: boolean;
      setForceDriverLicenseExpireCheckout: boolean;
      forceDriverLicenseFieldsCheckout: boolean;
      forceDriverBirthdateFieldCheckout: boolean;
      dynamicTimeCarBooking: boolean;
      ordererEmail: boolean;
      bisnodeSearchCustomer: boolean;
      bisnodeSearchPhoneAndZipCode: boolean;
      bisnodeSearchSocialSecurityNumber: boolean;
      bisnodeSearchRegnr: boolean;
      onlyEmailCheckin: boolean;
      invoiceTextFromPayer: boolean;
      allowCheckinBeforeToday: boolean;
      locationAdmin: boolean;
      payerAdmin: boolean;
      locationUser: boolean;
      connections: boolean;
      reservationMobileNumberRequired: boolean;
      reservationEmailAddressRequired: boolean;
      checkoutEmailAddressRequired: boolean;
      guaranteedCarDelivery: boolean;
      showManualPickupPlaceDeafultText: boolean;
      showRentalAgreementTextToCheckin: boolean;
      canDuplicateReservation: boolean;
      ccByOcOnly: boolean;
      sendPaymentLink: boolean;
      checkoutEmailMandatory: boolean;
      regNumberMandatoryForManualPayer: boolean;
      regNumberMandatoryForRegularPayer: boolean;
      reservationEmailMandatory: boolean;
      reservationMobileNumberMandatory: boolean;
      forceDriverLicenseExpireCheckout: boolean;
    };
    reservationEarliestInHours: number;
    roles: string[];
    firstName: null;
    lastName: null;
    email: string | null;
    telephone: string | null;
    mobileNumber: string | null;
    company: string;
    companyAddress: string | null;
    companyPostcode: string | null;
    companyCity: string | null;
    companyTelephone: string | null;
    companyContactPerson: string | null;
    socialSecurityNumber: string | null;
    imageUrl: string;
    facebookUrl: null;
    blogUrl: null;
    city: null;
    twitterUrl: null;
    pickupPlaceStation: string | null;
    pickupPlaceId: number | null;
    rightsHash: string;
    sign: null;
    reservationDaysOffset: number;
    reservationDefaultTime: null;
    useNewLocations: boolean;
    locationId: null;
    connectionsPayerId: null;
    logoPath: null;
    locationAdmin: boolean;
    priceListFilename: null;
    accountActive: boolean;
    findStationsFromPickupPlaces: boolean;
    info: null;
    locale: {
      lang: string;
      country: string;
    };
  } | null;
  error: string | null;
}

const initialState: UserState = {
  user: null,
  error: null,
};

const userSlice: Slice<UserState> = createSlice({
  name: 'user',
  initialState,
  reducers: {
    loginSuccess(
      state,
      action: PayloadAction<{
        signedIn: boolean;
        id: number;
        ebookId: number;
        name: string;
        country: {
          name: string;
          isocode: string;
          timezone: string;
        };
        station: null;
        stations: null;
        username: string;
        password: null;
        permissions: {
          selectReturnLocation: boolean;
          locationMap: boolean;
          showOnlyCarsForOpenLocations: boolean;
          showOnlyCarsWithKeyboxcode: boolean;
          mapSelectCarReservation: boolean;
          mapSelectCarAgreement: boolean;
          showPriceTable: boolean;
          ignoreOpeningHours: boolean;
          sendAgreementExtraConfirmation: boolean;
          manualPayerInput: boolean;
          manualReturnLocationAddressAllowed: boolean;
          manualPickupLocationAddressAllowed: boolean;
          advancedCheckin: boolean;
          showEmailField: boolean;
          showCheckoutMobileNumberField: boolean;
          showReservationMobileNumberField: boolean;
          allowSelectChargedCarGroup: boolean;
          cdpInput: boolean;
          hccInput: boolean;
          tariffInput: boolean;
          voucherAccountNumberInput: boolean;
          voucherAmountInput: boolean;
          voucherReferenceInput: boolean;
          voucherNumberInput: boolean;
          licenseNumberInput: boolean;
          pciCreditCardInput: boolean;
          equipment: boolean;
          reservationToAgreement: boolean;
          carSearch: boolean;
          driverInsurance: boolean;
          checkoutCarFirst: boolean;
          showEstimatedPrice: boolean;
          reservationPayerRequired: boolean;
          workshopAnalysis: boolean;
          showDriverLicenseInputField: boolean;
          setForceDriverLicenseExpireCheckout: boolean;
          forceDriverLicenseFieldsCheckout: boolean;
          forceDriverBirthdateFieldCheckout: boolean;
          dynamicTimeCarBooking: boolean;
          ordererEmail: boolean;
          bisnodeSearchCustomer: boolean;
          bisnodeSearchPhoneAndZipCode: boolean;
          bisnodeSearchSocialSecurityNumber: boolean;
          bisnodeSearchRegnr: boolean;
          onlyEmailCheckin: boolean;
          invoiceTextFromPayer: boolean;
          allowCheckinBeforeToday: boolean;
          locationAdmin: boolean;
          payerAdmin: boolean;
          locationUser: boolean;
          connections: boolean;
          reservationMobileNumberRequired: boolean;
          reservationEmailAddressRequired: boolean;
          checkoutEmailAddressRequired: boolean;
          guaranteedCarDelivery: boolean;
          showManualPickupPlaceDeafultText: boolean;
          showRentalAgreementTextToCheckin: boolean;
          canDuplicateReservation: boolean;
          ccByOcOnly: boolean;
          sendPaymentLink: boolean;
          checkoutEmailMandatory: boolean;
          regNumberMandatoryForManualPayer: boolean;
          regNumberMandatoryForRegularPayer: boolean;
          reservationEmailMandatory: boolean;
          reservationMobileNumberMandatory: boolean;
          forceDriverLicenseExpireCheckout: boolean;
        };
        reservationEarliestInHours: number;
        roles: string[];
        firstName: null;
        lastName: null;
        email: null;
        telephone: null;
        mobileNumber: null;
        company: string;
        companyAddress: null;
        companyPostcode: null;
        companyCity: null;
        companyTelephone: string | null;
        companyContactPerson: null;
        socialSecurityNumber: null;
        imageUrl: string;
        facebookUrl: null;
        blogUrl: null;
        city: null;
        twitterUrl: null;
        pickupPlaceStation: null;
        pickupPlaceId: null;
        rightsHash: string;
        sign: null;
        reservationDaysOffset: number;
        reservationDefaultTime: null;
        useNewLocations: boolean;
        locationId: null;
        connectionsPayerId: null;
        logoPath: null;
        locationAdmin: boolean;
        priceListFilename: null;
        accountActive: boolean;
        findStationsFromPickupPlaces: boolean;
        info: null;
        locale: {
          lang: string;
          country: string;
        };
      }>
    ) {
      state.user = action.payload;
      state.error = null;
    },
    setUser: (state, action: PayloadAction<UserState['user']>) => {
      state.user = { ...state.user, ...action.payload };
    },
    setMobileNumber: (state, action: PayloadAction<string>) => {
      state.user.mobileNumber = action.payload;
    },

    setName: (state, action: PayloadAction<string>) => {
      state.user.name = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.user.email = action.payload;
    },
    setCompanyAddress: (state, action: PayloadAction<string>) => {
      state.user.companyAddress = action.payload;
    },
    setCompanyPostcode: (state, action: PayloadAction<string>) => {
      state.user.companyPostcode = action.payload;
    },
    setCompanyCity: (state, action: PayloadAction<string>) => {
      state.user.companyCity = action.payload;
    },
    setTelephone: (state, action: PayloadAction<string>) => {
      state.user.telephone = action.payload;
    },
    setCompanyTelephone: (state, action: PayloadAction<string>) => {
      state.user.companyTelephone = action.payload;
    },
    setCompanyContactPerson: (state, action: PayloadAction<string>) => {
      state.user.companyContactPerson = action.payload;
    },
    setPickupPlaceStation: (state, action: PayloadAction<string>) => {
      state.user.pickupPlaceStation = action.payload;
    },
    setStation: (state, action: PayloadAction<string>) => {
      state.user.station = action.payload;
    },
    setStations: (state, action: PayloadAction<Stations>) => {
      state.user.stations = action.payload;
    },
    logout(state) {
      state.user = null;
      state.error = null;
      localStorage.removeItem('persistor');
    },
    loginFailure(state, action: PayloadAction<string>) {
      state.user = null;
      state.error = action.payload;
    },
  },
});

export const {
  setUser,
  setMobileNumber,
  setName,
  setEmail,
  setCompanyAddress,
  setCompanyPostcode,
  setCompanyCity,
  setTelephone,
  setCompanyContactPerson,
  setCompanyTelephone,
  setPickupPlaceStation,
  loginSuccess,
  logout,
  loginFailure,
  setStations,
} = userSlice.actions;

export default userSlice.reducer;
